import { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import fieldTrim from "../../supportFunctions/FieldTrim";
import { ContractingCompany, Person, UserWithPerson } from "./Person";
import UsersService from "@velocity-ehs/user-utils-module";
import EmploymentDetailsCard from "./cards/EmploymentDetailsCard";
import { PersonDetailsCard } from "./cards/PersonDetailsCard";
import { mapCurrentPersonToForm } from "./EditPerson";
import { UnsavedChangesPrompt } from "@velocity-ehs/component-library";
import WhereCard from "./cards/WhereCard";
import i18next from "i18next";
import { AdditionalInformation } from "./cards/AdditionalInformation";
import { ContactInformation } from "./cards/ContactInformation";
import { useGetPerson } from "../../../hooks/useGetPerson";
import { useGetSupervisors } from "../../../hooks/useGetSupervisors";
import { useGetContractingCompanies } from "../../../hooks/useGetContractingCompanies";
import { ContractingCompanySidebar } from "../ContractingCompanies/ContractingCompanySidebar";

export enum Mode {
  Add = "Add",
  Edit = "Edit",
  View = "View",
}

interface PersonFormProps {
  mode: Mode;
  isLoading?: boolean;
  fromPersonProfile?: boolean;
  formMethods: UseFormReturn<Person, any>;
  personUpdateCallback?: (updatedPerson: Person) => void;
  onSubmit?: (formValues: Person) => void;
}

const PersonForm: React.FC<PersonFormProps> = ({
  mode = Mode.View,
  isLoading = false,
  fromPersonProfile = false,
  formMethods,
  personUpdateCallback,
  onSubmit: onSubmitCallback,
}) => {
  const history = useHistory();

  const { id } = useParams() as { id: string };

  const [showContractingCompanySlider, setShowContractingCompanySlider] =
    useState<boolean>(false);

  const closeSlider = () => {
    setShowContractingCompanySlider(false);
  };

  const openSlider = () => {
    setShowContractingCompanySlider(true);
  };

  const {
    person,
    isLoading: isPersonLoading,
    isFetchedAfterMount,
  } = useGetPerson(id);

  const modificationMode: Mode = mode;

  const { supervisors } = useGetSupervisors();

  const { contractingCompanies } = useGetContractingCompanies();

  //fall-back isDirty if the form has been reset and the mutation fails
  const [isDirty, setIsDirty] = useState(false);

  const handleSliderSubmit = (createdCompany: ContractingCompany) => {
    contractingCompanies.push(createdCompany);
    formMethods.setValue("contractingCompany", createdCompany);
  };

  // Strip out unsaved form changes when PersonForm is remounted due to EditPerson detail page navigation
  useEffect(() => {
    if (person) {
      formMethods.reset(mapCurrentPersonToForm(person));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPersonLoading, isFetchedAfterMount]);

  const { handleSubmit, formState } = formMethods;

  const savePersonForm = (formValues: UserWithPerson) => {
    //reset form so the prompt does not show after saving
    formMethods.reset(formMethods.getValues());

    return onSubmitCallback(formValues);
  };

  const onSubmit = useCallback(
    (formValues: Person) => {
      //If isDirty was set to true, the form should no longer be "dirty" at this point.
      setIsDirty(false);
      const trimmedFormValues = fieldTrim({ formData: formValues });

      return savePersonForm(trimmedFormValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [person]
  );

  // TODO: Ask Maria for all of the fields that are considered sensitive data
  const isReadOnly =
    !UsersService.permissions.canEditSensitiveData ||
    modificationMode === Mode.View;

  return (
    <>
      <form id="personForm" onSubmit={handleSubmit(onSubmit)}>
        <>
          <PersonDetailsCard
            formMethods={formMethods}
            readOnly={modificationMode === Mode.View}
            canViewSensitiveData={UsersService.permissions.canViewSensitiveData}
            isLoading={isLoading}
            isSensitiveDataReadOnly={isReadOnly}
            contractingCompanies={contractingCompanies}
            mode={modificationMode}
            isUser={person?.userId !== null}
            openSlider={openSlider}
          />
          <WhereCard
            formMethods={formMethods}
            readOnly={modificationMode === Mode.View}
            isLoading={isLoading}
          />
          <AdditionalInformation
            formMethods={formMethods}
            isLoading={isLoading}
            readOnly={isReadOnly}
          />
          <EmploymentDetailsCard
            formMethods={formMethods}
            readOnly={modificationMode === Mode.View}
            canViewSensitiveData={UsersService.permissions.canViewSensitiveData}
            isLoading={isLoading}
            personCustomerRelationshipOptions={PersonCustomerRelationship()}
            isSensitiveDataReadOnly={isReadOnly}
            supervisorOptions={supervisors}
          />
          <ContactInformation
            formMethods={formMethods}
            isLoading={isLoading}
            readOnly={modificationMode === Mode.View}
            isSensitiveDataReadOnly={isReadOnly}
            isUser={person?.userId !== null}
            mode={mode}
          />
        </>
      </form>
      <ContractingCompanySidebar
        isVisible={showContractingCompanySlider}
        closeSlider={closeSlider}
        updateSelectedContractingCompany={handleSliderSubmit}
      />
      <UnsavedChangesPrompt
        isDirty={formState.isDirty || isDirty}
        history={history}
      />
    </>
  );
};

export const PersonCustomerRelationship = () => [
  { label: i18next.t("universal_Employee"), value: "EMPLOYEE", id: "EMPLOYEE" },
  {
    label: i18next.t("universal_Contractor"),
    value: "CONTRACTOR",
    id: "CONTRACTOR",
  },
  {
    label: i18next.t("universal_Member_of_the_public"),
    value: "MEMBER OF THE PUBLIC",
    id: "MEMBER OF THE PUBLIC",
  },
];

export default PersonForm;
